import React from 'react'
import styled from 'styled-components'
import { Callout } from 'sutra/components/Callout/Callout'
import { gray } from 'sutra/tokens/colors'
import { radii } from 'sutra/tokens/radii'
import { spacing } from 'sutra/tokens/spacing'
import { typography } from 'sutra/tokens/typography'

import {
  AGENT_ANALYSIS_WINDOW_VALUE,
  BENCHMARKING_ANALYSIS_WINDOW_VALUE,
} from 'features/trend-detection/constants'
import type { AgentType } from 'features/trend-detection/types'

// Per-type analysis window copy. Each type's run cadence is fixed today and
// not user-editable — see the concept matrix in the create-agent-flow plan.
// Issue/Benchmarking reuse the same constants the (now-deleted) review step
// rendered; Reports has no fixed window, so its copy names the trigger
// instead (data refresh) rather than inventing a cadence it doesn't have.
const WINDOW_LABEL: Record<AgentType, string> = {
  issue_detection: AGENT_ANALYSIS_WINDOW_VALUE,
  benchmarking: BENCHMARKING_ANALYSIS_WINDOW_VALUE,
  general: 'Runs each time your project data refreshes',
}

const CALLOUT_TEXT: Record<AgentType, string> = {
  issue_detection: 'All agents default to 6 weeks. This cannot yet be changed.',
  benchmarking:
    'Benchmarking agents run on a rolling 3-month window. This cannot yet be changed.',
  general:
    'This agent runs each time your project data refreshes. This cannot yet be changed.',
}

export interface TriggerSectionProps {
  agentType: AgentType
}

/** A disabled analysis-window control plus an info callout reminding the
 * user of a real constraint on the form they're filling out right now.
 * Nothing here is user-editable yet — see `WINDOW_LABEL` / `CALLOUT_TEXT`. */
export const TriggerSection: React.FC<TriggerSectionProps> = ({
  agentType,
}) => (
  <Wrap>
    <DisabledControl>{WINDOW_LABEL[agentType]}</DisabledControl>
    <Callout variant="info">{CALLOUT_TEXT[agentType]}</Callout>
  </Wrap>
)

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]}px;
`

const DisabledControl = styled.div`
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 ${spacing[3]}px;
  border-radius: ${radii.sm}px;
  border: 1px solid ${gray[200]};
  background: ${gray[50]};
  color: ${gray[500]};
  font-family: ${typography.fontFamily};
  font-size: 13px;
`
